import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import Box from '@mui/material/Box';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import CustomerIcon from '@mui/icons-material/Person';
import LocationIcon from '@mui/icons-material/StoreMallDirectory';
import HomeIcon from '@mui/icons-material/Home';
import ShoppingCart from '@mui/icons-material/ShoppingCart';
import Feedback from '@mui/icons-material/Feedback';
import VadoLogo from './vadologo';
import ReportIcon from '@mui/icons-material/BarChart';
import ListIcon from '@mui/icons-material/List';
import HistoryIcon from '@mui/icons-material/History';
import DescriptionIcon from '@mui/icons-material/Description';
import packageInfo from '../../package.json';
import { rbacmenuitems } from '../rbac/rbacrules';
import RbacMenuItemAuth from '../rbac/rbacmenuitemauth';
import BuildIcon from '@mui/icons-material/Build';
import Tooltip from '@mui/material/Tooltip';
import { selectPortfolio } from '../modules/portfoliodata';
import { setShowcaseMode } from '../modules/appstate';
import SearchField from './search';
import RbacFragment from '../rbac/rbacfragment';
import User from '../domain/user';
import CONFIG from '../config';
import languages from '../i18n/languages';
import { useTranslation } from 'react-i18next';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import TenantSwitcher from './tenantswitcher';

function MenuBar(props) {
    const dispatch = useDispatch();
    const history = useHistory();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [anchorPortfolioEl, setAnchorPortfolioEl] = React.useState(null);
    const [anchorLanguageEl, setAnchorLanguageEl] = React.useState(null);
    const [anchorShowcaseModeEl, setAnchorShowcaseModeEl] = React.useState(null);
    const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState(null);
    const [selectedView, setSelectedView] = React.useState('');
    const showcaseMode = useSelector((state) => state.appstate.showcaseMode);
    const { t, i18n } = useTranslation();
    const isMenuOpen = Boolean(anchorEl);
    const isPortfolioMenuOpen = Boolean(anchorPortfolioEl);
    const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);
    const isLanguageSelectOpen = Boolean(anchorLanguageEl);
    const isShowcaseModeSelectOpen = Boolean(anchorShowcaseModeEl);
    const features = useSelector((state) => state.settingsdata.features);
    const surveysInUse = features != null ? features.surveysInUse : false;

    const selectedLang = i18n.language;

    const handleProfileMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handlePortfolioMenuOpen = (event) => {
        setAnchorPortfolioEl(event.currentTarget);
    };

    const handleLanguageMenuOpen = (event) => {
        setAnchorLanguageEl(event.currentTarget);
    };

    const handleShowcaseModeMenuOpen = (event) => {
        setAnchorShowcaseModeEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setAnchorLanguageEl(null);
        setAnchorPortfolioEl(null);
        handleMobileMenuClose();
    };

    const handlePortfolioMenuClose = () => {
        setAnchorPortfolioEl(null);
    };

    const handleLanguageMenuClose = () => {
        setAnchorLanguageEl(null);
    };

    const handleShowcaseModeMenuClose = () => {
        setAnchorShowcaseModeEl(null);
    };

    const handleMobileMenuClose = () => {
        setMobileMoreAnchorEl(null);
    };

    const onClickLocations = () => {
        history.push('/locations');
        setSelectedView('locations');
        handleMobileMenuClose();
    };

    const onClickSurveys = () => {
        history.push('/surveys');
        setSelectedView('surveys');
        handleMobileMenuClose();
    };

    const onClickOrderHistory = () => {
        //TODO: update correct path and view
        history.push('/orderhistory');
        setSelectedView('orderhistory');
        handleMobileMenuClose();
    };

    const onClickHome = () => {
        history.push('/');
        setSelectedView('home');
        handleMobileMenuClose();
    };

    const onClickCustomers = () => {
        history.push('/customers');
        setSelectedView('customers');
        handleMobileMenuClose();
    };

    const onClickShoppingCart = () => {
        history.push('/basket');
        setSelectedView('basket');
        handleMobileMenuClose();
    };

    const onClickReports = () => {
        history.push('/reports');
        setSelectedView('reports');
        handleMobileMenuClose();
    };

    const onClickProducts = () => {
        history.push('/products');
        setSelectedView('products');
        handleMobileMenuClose();
    };

    const onClickProduction = () => {
        history.push('/production');
        setSelectedView('production');
        handleMobileMenuClose();
    };

    const onClickInvoicing = () => {
        history.push('/invoices');
        setSelectedView('invoices');
        handleMobileMenuClose();
    };

    const onClickWarehouses = () => {
        history.push('/warehouses');
        setSelectedView('warehouses');
        handleMobileMenuClose();
    };

    const onClickAdmin = () => {
        history.push('/admin');
        setSelectedView('admin');
        handleMobileMenuClose();
    };

    const handleLogout = () => {
        history.push('/logout');
        handleMenuClose();
    };

    const openMobileMenu = (event) => {
        setMobileMoreAnchorEl(event.currentTarget);
    };

    const getMenuButtonColor = (view) => {
        if (selectedView === view) return 'secondary';
        if (props.location && props.location.pathname.indexOf(view) !== -1) return 'secondary';
        return 'inherit';
    };

    const handleFeedbackOpen = () => {
        history.push('/feedback');
    };

    const handleUserDataOpen = () => {
        history.push('/user');
        handleMenuClose();
    };

    const openDebugData = () => {
        history.push('/debug');
        handleMenuClose();
    };

    const changeLanguage = (newLang) => {
        document.cookie = `lang=${newLang};max-age=31536000;path=/`;
        i18n.changeLanguage(newLang);
    };

    const renderProfileMenu = (
        <Menu
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={isMenuOpen}
            onClose={handleMenuClose}>
            <MenuItem>
                {t('menu.version')} {packageInfo.version}
            </MenuItem>
            <MenuItem onClick={handleMenuClose}>
                {props.profile.name}
                <br />
                {props.profile.organization}
            </MenuItem>
            <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.UserProfile}>
                <MenuItem onClick={handleUserDataOpen}>{t('menu.ownInfo')}</MenuItem>
            </RbacMenuItemAuth>
            <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.Portfolio}>
                <MenuItem onClick={handlePortfolioMenuOpen}>{t('general.portfolio')}</MenuItem>
            </RbacMenuItemAuth>
            <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.ShowcaseMode}>
                <MenuItem onClick={handleShowcaseModeMenuOpen}>{t('menu.showcaseMode')}</MenuItem>
            </RbacMenuItemAuth>

            <MenuItem onClick={handleLanguageMenuOpen}>{t('general.language')}</MenuItem>

            <MenuItem onClick={handleLogout}>{t('menu.logout')}</MenuItem>

            {CONFIG.features.debugUi && <MenuItem onClick={openDebugData}>Debug</MenuItem>}
        </Menu>
    );

    const renderLanguageSelect = (
        <Menu
            anchorEl={anchorLanguageEl}
            open={isLanguageSelectOpen}
            onClose={handleLanguageMenuClose}>
            {languages.map((lang) => (
                <MenuItem
                    key={lang.lang}
                    sx={selectedLang === lang.lang ? { fontWeight: 900 } : {}}
                    onClick={() => {
                        changeLanguage(lang.lang);
                        handleLanguageMenuClose();
                    }}>
                    <span style={{ margin: '5px 5px 5px 0px' }}>{lang.flagImg}</span>
                    {t(lang.name)}
                </MenuItem>
            ))}
        </Menu>
    );

    const renderPortfolioMenu = (
        <Menu
            anchorEl={anchorPortfolioEl}
            open={isPortfolioMenuOpen}
            onClose={handlePortfolioMenuClose}>
            {props.profile.portfolioDescriptions.map((p) => (
                <MenuItem
                    key={p.id}
                    sx={props.profile.selectedPortfolioId === p.id ? { fontWeight: 900 } : {}}
                    onClick={() => { selectPortfolio(p.id)(dispatch); handlePortfolioMenuClose(); handleMenuClose(); }}>
                    {p.name}
                </MenuItem>
            ))}
        </Menu>
    );

    const renderShowcaseModeMenu = (
        <Menu
            anchorEl={anchorShowcaseModeEl}
            open={isShowcaseModeSelectOpen}
            onClose={handleShowcaseModeMenuClose}>
            <MenuItem
                sx={showcaseMode ? {} : { fontWeight: 900 }}
                onClick={() => { setShowcaseMode(false)(dispatch); handleShowcaseModeMenuClose(); }}>
                {'Pois'}
            </MenuItem>
            <MenuItem
                sx={showcaseMode ? { fontWeight: 900 } : {}}
                onClick={() => { setShowcaseMode(true)(dispatch); handleShowcaseModeMenuClose(); }}>
                {'Päällä'}
            </MenuItem>
        </Menu>
    );

    const renderMobileMenu = (
        <Menu
            sx={{ zIndex: 20 }}
            anchorEl={mobileMoreAnchorEl}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={isMobileMenuOpen}
            onClose={handleMenuClose}
            PaperProps={{ sx: { maxHeight: '80vh', overflowY: 'auto' } }}>
            <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.Home}>
                <MenuItem onClick={onClickHome}>
                    <IconButton color="inherit">
                        <HomeIcon />
                    </IconButton>
                    {t('menu.frontpage')}
                </MenuItem>
            </RbacMenuItemAuth>
            <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.Customers}>
                <MenuItem onClick={onClickCustomers}>
                    <IconButton color="inherit">
                        <CustomerIcon />
                    </IconButton>
                    {t('general.customers')}
                </MenuItem>
            </RbacMenuItemAuth>
            <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.Locations}>
                <MenuItem onClick={onClickLocations}>
                    <IconButton color="inherit">
                        <LocationIcon />
                    </IconButton>
                    {t('general.locations')}
                </MenuItem>
            </RbacMenuItemAuth>
            {surveysInUse && (
                <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.Surveys}>
                    <MenuItem onClick={onClickSurveys}>
                        <IconButton color="inherit">
                            <Badge badgeContent={props.surveysCount} color="secondary" overlap="rectangular">
                                <DescriptionIcon />
                            </Badge>
                        </IconButton>
                        {t('survey.surveys')}
                    </MenuItem>
                </RbacMenuItemAuth>
            )}
            <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.OrderHistory}>
                <MenuItem onClick={onClickOrderHistory}>
                    <IconButton color="inherit">
                        <HistoryIcon />
                    </IconButton>
                    {t('location.orderHistory')}
                </MenuItem>
            </RbacMenuItemAuth>
            <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.Reports}>
                <MenuItem onClick={onClickReports}>
                    <IconButton color="inherit">
                        <ReportIcon />
                    </IconButton>
                    {t('menu.reports')}
                </MenuItem>
            </RbacMenuItemAuth>
            <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.Products}>
                <MenuItem onClick={onClickProducts}>
                    <IconButton color="inherit">
                        <ListIcon />
                    </IconButton>
                    {t('general.products')}
                </MenuItem>
            </RbacMenuItemAuth>

            <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.Production}>
                <MenuItem onClick={onClickProduction}>
                    <IconButton color="inherit">
                        <i className="fas fa-industry"></i>
                    </IconButton>
                    {t('menu.production')}
                </MenuItem>
            </RbacMenuItemAuth>
            <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.Invoices}>
                <MenuItem onClick={onClickInvoicing}>
                    <IconButton color="inherit">
                        <i className="fas fa-file-invoice-dollar"></i>
                    </IconButton>
                    {t('menu.invoicing')}
                </MenuItem>
            </RbacMenuItemAuth>

            <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.ShoppingCart}>
                <MenuItem onClick={onClickShoppingCart}>
                    <IconButton color="inherit">
                        {props.shoppingCartContent != null && (
                            <Badge badgeContent={props.shoppingCartContent} color="secondary" overlap="rectangular">
                                <ShoppingCart />
                            </Badge>
                        )}
                        {props.shoppingCartContent == null && <ShoppingCart />}
                    </IconButton>
                    {t('menu.orders')}
                </MenuItem>
            </RbacMenuItemAuth>

            <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.Warehouses}>
                <MenuItem onClick={onClickWarehouses}>
                    <IconButton color="inherit">
                        <WarehouseIcon />
                    </IconButton>
                    Varastonhallinta
                </MenuItem>
            </RbacMenuItemAuth>

            <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.Feedback}>
                <MenuItem onClick={handleFeedbackOpen}>
                    <IconButton>
                        <Feedback style={{ color: 'black' }} />
                    </IconButton>
                    {t('general.feedback')}
                </MenuItem>
            </RbacMenuItemAuth>

            <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.Admin}>
                <MenuItem onClick={onClickAdmin}>
                    <IconButton color="inherit">
                        <BuildIcon />
                    </IconButton>
                    {t('menu.maintenance')}
                </MenuItem>
            </RbacMenuItemAuth>
        </Menu>
    );

    if (props.location && props.location.search === '?sidebar=false') return null;

    return (
        <Box sx={{ width: '100%' }}>
            <AppBar position="static">
                <Toolbar>
                    <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                        <IconButton
                            sx={{ ml: '-12px', mr: '20px' }}
                            color="inherit"
                            aria-label="Open drawer"
                            onClick={openMobileMenu}>
                            <MenuIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                        <VadoLogo />
                    </Box>

                    <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                        <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.Home}>
                            <Tooltip title={t('menu.frontpage')}>
                                <IconButton
                                    color={getMenuButtonColor('home')}
                                    aria-label="etusivu"
                                    onClick={onClickHome}>
                                    <HomeIcon />
                                </IconButton>
                            </Tooltip>
                        </RbacMenuItemAuth>

                        <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.Customers}>
                            <Tooltip title={t('general.customers')}>
                                <IconButton
                                    color={getMenuButtonColor('customers')}
                                    aria-label="Asiakkaat"
                                    name="asiakkaat"
                                    onClick={onClickCustomers}>
                                    <CustomerIcon />
                                </IconButton>
                            </Tooltip>
                        </RbacMenuItemAuth>

                        <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.Locations}>
                            <Tooltip title={t('general.locations')}>
                                <IconButton
                                    color={getMenuButtonColor('locations')}
                                    aria-label="Kohteet"
                                    onClick={onClickLocations}>
                                    <LocationIcon />
                                </IconButton>
                            </Tooltip>
                        </RbacMenuItemAuth>

                        {surveysInUse && (
                            <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.Surveys}>
                                <Tooltip title={t('survey.surveys')}>
                                    <IconButton
                                        color={getMenuButtonColor('surveys')}
                                        aria-label="Kartoitukset"
                                        onClick={onClickSurveys}>
                                        <Badge
                                            badgeContent={props.surveysCount}
                                            color="secondary"
                                            overlap="rectangular">
                                            <DescriptionIcon />
                                        </Badge>
                                    </IconButton>
                                </Tooltip>
                            </RbacMenuItemAuth>
                        )}

                        <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.OrderHistory}>
                            <Tooltip title={t('location.orderHistory')}>
                                <IconButton
                                    color={getMenuButtonColor('orderhistory')}
                                    aria-label="Kohteet"
                                    onClick={onClickOrderHistory}>
                                    <HistoryIcon />
                                </IconButton>
                            </Tooltip>
                        </RbacMenuItemAuth>

                        <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.Reports}>
                            <Tooltip title={t('menu.reports')}>
                                <IconButton
                                    color={getMenuButtonColor('reports')}
                                    aria-label="Raportit"
                                    onClick={onClickReports}>
                                    <ReportIcon />
                                </IconButton>
                            </Tooltip>
                        </RbacMenuItemAuth>

                        <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.Products}>
                            <Tooltip title={t('general.products')}>
                                <IconButton
                                    color={getMenuButtonColor('products')}
                                    aria-label="Tuotteet"
                                    onClick={onClickProducts}>
                                    <ListIcon />
                                </IconButton>
                            </Tooltip>
                        </RbacMenuItemAuth>

                        <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.Production}>
                            <Tooltip title={t('menu.production')}>
                                <IconButton
                                    color={getMenuButtonColor('production')}
                                    aria-label="Tuotanto"
                                    onClick={onClickProduction}>
                                    <i className="fas fa-industry"></i>
                                </IconButton>
                            </Tooltip>
                        </RbacMenuItemAuth>

                        <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.Invoices}>
                            <Tooltip title={t('menu.invoicing')}>
                                <IconButton
                                    color={getMenuButtonColor('invoices')}
                                    aria-label="Laskutus"
                                    onClick={onClickInvoicing}>
                                    <i className="fas fa-file-invoice-dollar"></i>
                                </IconButton>
                            </Tooltip>
                        </RbacMenuItemAuth>

                        <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.Warehouses}>
                            <Tooltip title="Varastonhallinta">
                                <IconButton
                                    color={getMenuButtonColor('warehouses')}
                                    aria-label="Varastonhallinta"
                                    onClick={onClickWarehouses}>
                                    <WarehouseIcon />
                                </IconButton>
                            </Tooltip>
                        </RbacMenuItemAuth>

                        <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.Admin}>
                            <Tooltip title={t('menu.maintenance')}>
                                <IconButton
                                    color={getMenuButtonColor('admin')}
                                    aria-label="Ylläpito"
                                    onClick={onClickAdmin}>
                                    <BuildIcon />
                                </IconButton>
                            </Tooltip>
                        </RbacMenuItemAuth>
                    </Box>

                    <RbacFragment user={props.profile} requiredRole={User.RoleUser()}>
                        <SearchField history={props.history} />
                    </RbacFragment>

                    <Box sx={{ flexGrow: 1 }} />
                    <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                        <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.ShoppingCart}>
                            <Tooltip title={t('menu.openOrders')}>
                                <IconButton color="inherit" onClick={onClickShoppingCart}>
                                    {props.shoppingCartContent != null && (
                                        <Badge
                                            badgeContent={props.shoppingCartContent}
                                            color="secondary"
                                            overlap="rectangular">
                                            <ShoppingCart />
                                        </Badge>
                                    )}
                                    {props.shoppingCartContent == null && <ShoppingCart />}
                                </IconButton>
                            </Tooltip>
                        </RbacMenuItemAuth>

                        <RbacMenuItemAuth user={props.profile} menuitem={rbacmenuitems.Feedback}>
                            <Tooltip title={t('general.giveFeedback')}>
                                <IconButton onClick={handleFeedbackOpen}>
                                    <Feedback style={{ color: 'black' }} />
                                </IconButton>
                            </Tooltip>
                        </RbacMenuItemAuth>
                    </Box>

                    <TenantSwitcher />

                    <Tooltip title={t('general.profile')}>
                        <IconButton
                            aria-owns={isMenuOpen ? 'material-appbar' : undefined}
                            aria-haspopup="true"
                            onClick={handleProfileMenuOpen}
                            color="inherit">
                            <AccountCircle />
                        </IconButton>
                    </Tooltip>
                </Toolbar>
            </AppBar>
            {renderProfileMenu}
            {renderPortfolioMenu}
            {renderLanguageSelect}
            {renderShowcaseModeMenu}
            {renderMobileMenu}
        </Box>
    );
}

export default MenuBar;
